import classNames from 'classnames';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  useGQLSearchContext,
  useClearAllFilters as useSearchClearAllFilters,
  useFiltersCount as useSearchFiltersCount,
  useToggleChip,
} from '@sorare/core/src/contexts/graphql-search';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import type { Filter } from '@sorare/core/src/lib/filters';
import { FILTERS, TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  isListFilterWidget,
  isRangeFilterWidget,
  useFormatFilterWidgetValue,
} from '#src/hooks/search/useFormatFilterWidgetValue/index.tsx';
import { useRelevantRefinements } from '#src/hooks/search/useRelevantRefinements.ts';
import { useClearAllFilters } from '#src/search/ClearAllFilters/index.tsx';
import { useFiltersCount } from '#src/search/FiltersManager/useFiltersCount.ts';
import { GQL_SEARCH_FILTERS } from '#src/searchCommonPlayer/AdvancedPlayerSearch/filters.tsx';

import { FilterChip } from './FilterChip/index.tsx';
import { ListActiveFilter } from './ListActiveFilter/index.tsx';
import { RangeActiveFilter } from './RangeActiveFilter/index.tsx';
import { VirtualToggleActiveFilters } from './VirtualToggleActiveFilters/index.tsx';
import type { RefinementItem } from './types.ts';

const Container = styled.div`
  display: flex;
  gap: var(--unit);
  flex-wrap: nowrap;
  overflow-x: auto;
  & > * {
    flex: none;
  }
  @media ${tabletAndAbove} {
    flex-wrap: wrap;
    & > * {
      flex: initial;
    }
    &.forceScroll {
      flex-wrap: nowrap;
      & > * {
        flex: none;
      }
    }
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ActiveFilter = ({ item }: { item: RefinementItem }) => {
  const { formatFilterWidgetValue } = useFormatFilterWidgetValue();

  if (
    [
      TOGGLE_FILTERS.creditEligibleFilter.attribute,
      TOGGLE_FILTERS.onSaleFilter.attribute,
      TOGGLE_FILTERS.bidToEarnFilter.attribute,
    ].includesValue(item.attribute)
  ) {
    const [label] = formatFilterWidgetValue(item);

    return (
      <FilterChip
        label={label}
        onClose={() => {
          item.refine(item.refinements?.[0]);
        }}
      />
    );
  }

  if (isListFilterWidget(item)) {
    return <ListActiveFilter item={item} />;
  }

  if (isRangeFilterWidget(item)) {
    return <RangeActiveFilter item={item} />;
  }

  return null;
};

export const ActiveFilters = ({ forceScroll }: { forceScroll?: boolean }) => {
  const { formatMessage } = useIntl();
  const filtersCount = useFiltersCount({ countSealed: false });
  const items = useRelevantRefinements();
  const {
    indexUiState: { exclusions = {} },
    setIndexUiState,
  } = useInstantSearch<ExtendedUIState>();
  const { query, clear } = useSearchBox();
  const clearAllFilters = useClearAllFilters();
  const setVirtualToggleFilters = useVirtualToggleManager();

  if (!filtersCount) {
    return null;
  }

  return (
    <Container className={classNames({ forceScroll })}>
      {!!query && (
        <FilterChip
          label={
            <FormattedMessage
              id="ActiveFilters.search"
              defaultMessage="Search: {query}"
              values={{ query }}
            />
          }
          onClose={() => {
            clear();
          }}
        />
      )}
      {items.map(item => (
        <ActiveFilter key={item.attribute} item={item as RefinementItem} />
      ))}
      {Object.entries(exclusions)
        .filter(([, value]) => value)
        .map(([key]) => {
          const filter = (Object.values(FILTERS) as Filter[]).find(
            f => f.alias === key
          );
          return (
            <FilterChip
              key={key}
              label={filter ? formatMessage(filter.title) : key}
              onClose={() => {
                setIndexUiState(uiState => ({
                  ...uiState,
                  exclusions: {
                    ...uiState.exclusions,
                    [key]: false,
                  },
                }));
              }}
            />
          );
        })}
      <VirtualToggleActiveFilters
        setVirtualToggleFilters={setVirtualToggleFilters}
      />
      <Button size="small" onClick={clearAllFilters} color="red">
        <FormattedMessage {...filters.clearAll} />
      </Button>
    </Container>
  );
};

export const BackendActiveFilters = ({
  forceScroll,
}: {
  forceScroll?: boolean;
}) => {
  const {
    query,
    activeFacets,
    facets,
    setSearchParams,
    getRefinementType,
    getRefinementApiAttribute,
  } = useGQLSearchContext()!;
  const toggleChip = useToggleChip();

  const filtersCount = useSearchFiltersCount();

  const clear = () => {
    setSearchParams({ q: [] });
  };
  const clearAllFilters = useSearchClearAllFilters();
  const items = Object.entries(activeFacets).filter(e => e[1].length);

  if (!filtersCount) {
    return null;
  }

  return (
    <Container className={classNames({ forceScroll })}>
      {!!query && (
        <FilterChip
          label={
            <FormattedMessage
              id="ActiveFilters.search"
              defaultMessage="Search: {query}"
              values={{ query }}
            />
          }
          onClose={() => {
            clear();
          }}
        />
      )}
      {items.map(([urlAttribute, values]) => {
        const refinementType = getRefinementType(urlAttribute);
        if (refinementType === 'virtual') {
          if (urlAttribute === GQL_SEARCH_FILTERS.leagueFilter) {
            const getLabel = (value: string) => {
              return facets[GQL_SEARCH_FILTERS.leagueFilter].values.find(
                e => e.value === value
              )?.label;
            };
            return (
              <ActiveFilter
                key={urlAttribute}
                item={
                  {
                    attribute: getRefinementApiAttribute(urlAttribute),
                    refine: ({ value }) => {
                      toggleChip({ value, attribute: urlAttribute });
                    },
                    refinements: values.map(value => ({
                      value,
                      label: getLabel(value),
                      type: 'disjunctive',
                    })),
                  } as RefinementItem<string>
                }
              />
            );
          }
          return null;
        }
        return (
          <ActiveFilter
            key={urlAttribute}
            item={
              {
                attribute: getRefinementApiAttribute(urlAttribute),
                refine: ({ value }) => {
                  toggleChip({ value, attribute: urlAttribute });
                },
                refinements:
                  refinementType === 'string'
                    ? values.map(value => ({
                        value,
                        label: value,
                        type: 'disjunctive',
                      }))
                    : values
                        .map((value, i) => ({
                          value,
                          label: value,
                          operator: i === 0 ? '>=' : '<=',
                          type: 'numeric',
                        }))
                        .filter(e => e.value),
              } as RefinementItem<string>
            }
          />
        );
      })}
      <Button size="small" onClick={clearAllFilters} color="red">
        <FormattedMessage {...filters.clearAll} />
      </Button>
    </Container>
  );
};
